import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 5000;

export default function useWebsocket<Data extends object>(
  websocketUrl: string | undefined,
  onDataReceived: (data: Data) => void,
) {
  const [websocket, setWebsocket] = useState<WebSocket>();
  const retryCount = useRef(0);
  const retryTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!websocketUrl) return;

    retryCount.current = 0;
    let isCleanedUp = false;
    let currentWs: WebSocket;

    function connect() {
      const ws = new WebSocket(websocketUrl);
      currentWs = ws;

      ws.onopen = () => {
        retryCount.current = 0;
        setWebsocket(ws);
      };

      ws.onclose = () => {
        if (isCleanedUp) {
          return;
        }

        setWebsocket(undefined);

        if (retryCount.current < MAX_RETRIES) {
          retryCount.current += 1;
          retryTimeout.current = setTimeout(
            connect,
            BASE_DELAY_MS * 2 ** retryCount.current,
          );
        } else {
          toast.error(
            "Loading unread messages failed, please refresh the page",
          );
        }
      };
    }

    connect();

    return () => {
      isCleanedUp = true;
      clearTimeout(retryTimeout.current);
      currentWs?.close();
      setWebsocket(undefined);
    };
  }, [websocketUrl]);

  useEffect(() => {
    if (websocket) {
      function onMessage(event: MessageEvent) {
        //data is always assumed to be json
        const data = JSON.parse(event.data) as Data;
        onDataReceived(data);
      }

      websocket.addEventListener("message", onMessage);
      return () => {
        websocket.removeEventListener("message", onMessage);
      };
    }
  }, [websocket, onDataReceived]);

  return { websocket };
}
